/**
 * WhatsApp Message Templates Service
 *
 * Generates pre-formatted WhatsApp messages for driver invoices.
 */

export interface InvoiceTicketSummary {
  pcnNumber: string;
  vrn: string;
  council: string;
  amount?: number;
}

export interface DriverInvoiceMessageData {
  driverName: string;
  invoiceNumber: string;
  totalAmount: number;
  dueDate: string; // Format: "DD MMM YYYY" (e.g., "25 Jan 2026")
  invoiceUrl: string;
  organisationName: string;
  organisationPhone?: string;
  paymentLinkLabel?: string;
  ticketDetails?: InvoiceTicketSummary[];
}

export interface PaymentReminderMessageData {
  driverName: string;
  invoiceNumber: string;
  totalAmount: number;
  dueDate: string;
  daysOverdue?: number; // If positive, invoice is overdue
  lateFee?: number;
  invoiceUrl: string;
  organisationName: string;
  paymentLinkLabel?: string;
  ticketDetails?: InvoiceTicketSummary[];
}

export interface PaymentConfirmationMessageData {
  driverName: string;
  invoiceNumber: string;
  totalAmount: number;
  receiptUrl?: string;
  organisationName: string;
  ticketDetails?: InvoiceTicketSummary[];
}

const formatTicketSection = (ticketDetails?: InvoiceTicketSummary[]): string => {
  if (!ticketDetails || ticketDetails.length === 0) return '';

  const lines = ticketDetails.map((ticket, idx) => {
    const amountText = typeof ticket.amount === 'number' ? ` | GBP ${ticket.amount.toFixed(2)}` : '';
    return `${idx + 1}. PCN ${ticket.pcnNumber} | VRN ${ticket.vrn} | Council ${ticket.council}${amountText}`;
  });

  return `Tickets in this invoice:\n${lines.join('\n')}`;
};

export const MessageTemplates = {
  /**
   * Generate invoice notification message
   */
  generateInvoiceMessage(data: DriverInvoiceMessageData): string {
    const {
      driverName,
      invoiceNumber,
      totalAmount,
      dueDate,
      invoiceUrl,
      organisationName,
      organisationPhone,
      paymentLinkLabel,
      ticketDetails
    } = data;

    const ticketSection = formatTicketSection(ticketDetails);
    const paymentLabel = paymentLinkLabel || 'Pay online';

    return `Hi ${driverName},

Your invoice ${invoiceNumber} for GBP ${totalAmount.toFixed(2)} is ready.
${ticketSection ? `\n${ticketSection}` : ''}

Due date: ${dueDate}

${paymentLabel}: ${invoiceUrl}

${organisationPhone ? `Questions? Call us at ${organisationPhone}` : 'Questions? Reply to this message.'}

- ${organisationName}`;
  },

  /**
   * Generate payment reminder message
   */
  generatePaymentReminderMessage(data: PaymentReminderMessageData): string {
    const {
      driverName,
      invoiceNumber,
      totalAmount,
      dueDate,
      daysOverdue,
      lateFee,
      invoiceUrl,
      organisationName,
      paymentLinkLabel,
      ticketDetails
    } = data;

    const ticketSection = formatTicketSection(ticketDetails);
    const paymentLabel = paymentLinkLabel || 'Pay now';
    const ticketBlock = ticketSection ? `\n${ticketSection}\n` : '\n';

    if (daysOverdue && daysOverdue > 0) {
      return `Hi ${driverName},

Invoice ${invoiceNumber} for GBP ${totalAmount.toFixed(2)} is ${daysOverdue} day${daysOverdue > 1 ? 's' : ''} overdue.${ticketBlock}
Original due date: ${dueDate}
${lateFee && lateFee > 0 ? `Late fee: GBP ${lateFee.toFixed(2)}\n` : ''}
${paymentLabel} to avoid additional charges: ${invoiceUrl}

- ${organisationName}`;
    }

    const daysUntilDue = daysOverdue ? Math.abs(daysOverdue) : 0;
    return `Hi ${driverName},

Reminder: Invoice ${invoiceNumber} for GBP ${totalAmount.toFixed(2)} is due ${daysUntilDue > 0 ? `in ${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''}` : 'today'}.${ticketBlock}
Due date: ${dueDate}

${paymentLabel}: ${invoiceUrl}

Avoid late fees by paying today.

- ${organisationName}`;
  },

  /**
   * Generate payment confirmation message
   */
  generatePaymentConfirmationMessage(data: PaymentConfirmationMessageData): string {
    const { driverName, invoiceNumber, totalAmount, receiptUrl, organisationName, ticketDetails } = data;
    const ticketSection = formatTicketSection(ticketDetails);

    return `Hi ${driverName},

Payment received! Invoice ${invoiceNumber} for GBP ${totalAmount.toFixed(2)} is now paid.
${ticketSection ? `\n${ticketSection}` : ''}

${receiptUrl ? `Receipt: ${receiptUrl}` : ''}

Thank you!
- ${organisationName}`;
  },

  /**
   * Copy text to clipboard
   */
  async copyToClipboard(message: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(message);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      try {
        const textArea = document.createElement('textarea');
        textArea.value = message;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        return false;
      }
    }
  },

  /**
   * Format date to "DD MMM YYYY" format for WhatsApp messages
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  },

  /**
   * Calculate days until/overdue from due date
   */
  calculateDaysOverdue(dueDate: string): number {
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - due.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }
};
